import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  chooseSlotPhoto,
  fetchSlotPhotoObjectUrl,
  listSlotPhotos,
  uploadSlotPhoto,
  type SlotPhotoRow,
} from "../api/rest";
import { ROLE_RANK, useSiteStore } from "../stores/siteStore";
import styles from "./SlotPhoto.module.css";

/**
 * Photographic evidence of what stands at an address (ADR 272).
 *
 * <p>The bytes are behind a tenant-scoped endpoint, so an `<img src>` cannot
 * reach them — a bare tag carries no bearer token (ADR 204, the Minimap's raster
 * shipped without one and 401'd on every secured deployment). Every frame is
 * fetched with credentials and wrapped in an object URL, which the hook below
 * revokes on unmount. An un-revoked blob pins a multi-megabyte original for the
 * life of the document, and a run report opens dozens.
 *
 * <p>EXIF rotation is deliberately not applied here. The upload path stores the
 * original bytes untouched and records `exifOrientation` for consumers that are
 * not browsers; a browser applies it itself via the default
 * `image-orientation: from-image`. Rotating again would double-apply it.
 */

/** Fetch one photo's bytes as an object URL, revoked when the id changes or the
 *  component unmounts. Returns null while loading and on failure — the caller
 *  renders the difference.
 *
 *  The result is keyed by the photo it belongs to and the key compared at
 *  render, instead of resetting state synchronously inside the effect — the
 *  ADR 136 rule (react-hooks v7 makes that an ERROR): the only setState here
 *  is asynchronous, after the bytes land. A result from a previous photo is
 *  simply ignored by the key check, which is the same "loading" the reset
 *  used to produce, one render earlier. */
function usePhotoObjectUrl(siteId: string, warehouseId: string, id: string | null) {
  const key = id && siteId && warehouseId ? `${siteId}/${warehouseId}/${id}` : null;
  const [result, setResult] = useState<{ key: string; url: string | null; failed: boolean } | null>(null);

  useEffect(() => {
    if (!key || !id) return;
    let revoked = false;
    let created: string | null = null;
    fetchSlotPhotoObjectUrl(siteId, warehouseId, id)
      .then((u) => {
        // Unmounted before the bytes landed: revoke immediately rather than
        // handing an orphan URL to a dead component.
        if (revoked) { URL.revokeObjectURL(u); return; }
        created = u;
        setResult({ key, url: u, failed: false });
      })
      .catch(() => { if (!revoked) setResult({ key, url: null, failed: true }); });
    return () => {
      revoked = true;
      if (created) URL.revokeObjectURL(created);
    };
  }, [key, siteId, warehouseId, id]);

  const current = result && result.key === key ? result : null;
  return { url: current?.url ?? null, failed: current?.failed ?? false };
}

function Thumb({
  siteId, warehouseId, photo, onOpen,
}: { siteId: string; warehouseId: string; photo: SlotPhotoRow; onOpen: () => void }) {
  const { t } = useTranslation();
  const { url, failed } = usePhotoObjectUrl(siteId, warehouseId, photo.id);

  return (
    <button
      type="button"
      className={`${styles.thumbBtn} ${photo.chosen ? styles.thumbChosen : ""}`}
      onClick={onOpen}
      aria-label={
        photo.chosen
          ? t("slotPhoto.openChosen", { address: photo.address })
          : t("slotPhoto.open", { address: photo.address })
      }
    >
      {url
        ? <img className={styles.thumbImg} src={url} alt="" />
        : failed
          ? <span className={styles.thumbFailed}>{t("slotPhoto.failed")}</span>
          : <span className={styles.thumbPending}>{t("common.loading")}</span>}
      {photo.chosen && (
        <span className={styles.chosenBadge} aria-hidden="true">
          <svg width="9" height="9" viewBox="0 0 12 12" fill="none" aria-hidden="true">
            <path d="M2 6.5 4.8 9.2 10 3.4" stroke="currentColor" strokeWidth="2"
                  strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </span>
      )}
    </button>
  );
}

/** How the address on a frame is known, which is what separates evidence from a
 *  claim. `kit_association` is the only source bound to a read; everything else
 *  is a human or a heuristic and is marked as the weaker thing it is. */
function SourceBadge({ source }: { source: SlotPhotoRow["addressSource"] }) {
  const { t } = useTranslation();
  const strong = source === "kit_association";
  const weak = source === "manual" || source === "filename";
  return (
    <span className={`${styles.source} ${strong ? styles.sourceKit : weak ? styles.sourceWeak : ""}`}>
      {t(`slotPhoto.source.${source}`)}
    </span>
  );
}

function Lightbox({
  siteId, warehouseId, photos, index, onIndex, onClose,
}: {
  siteId: string; warehouseId: string; photos: SlotPhotoRow[];
  index: number; onIndex: (i: number) => void; onClose: () => void;
}) {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const closeRef = useRef<HTMLButtonElement>(null);
  const photo = photos[index];
  const { url, failed } = usePhotoObjectUrl(siteId, warehouseId, photo?.id ?? null);

  useEffect(() => { closeRef.current?.focus(); }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") { e.stopPropagation(); onClose(); }
      if (e.key === "ArrowRight" && index < photos.length - 1) onIndex(index + 1);
      if (e.key === "ArrowLeft" && index > 0) onIndex(index - 1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [index, photos.length, onIndex, onClose]);

  const choose = useMutation({
    mutationFn: () => chooseSlotPhoto(siteId, warehouseId, photo.id),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["slot-photos"] }); },
  });

  if (!photo) return null;

  const taken = photo.takenAt
    ? new Date(photo.takenAt).toLocaleString(i18n.language)
    : t("slotPhoto.noTimestamp");

  // Portalled to <body>: the strip renders inside the app shell's scrolling
  // `.main`, and a modal that has to cover the top bar and the nav rail should
  // not depend on that ancestor never gaining a transform or a stacking context.
  return createPortal(
    <div className={styles.backdrop} role="dialog" aria-modal="true"
         aria-label={t("slotPhoto.dialog", { address: photo.address })}>
      <div className={styles.lightHead}>
        <div>
          <div className={styles.lightAddress}>{photo.address}</div>
          <div className={styles.lightMeta}>
            {taken}
            {photo.widthPx && photo.heightPx ? ` · ${photo.widthPx}×${photo.heightPx}` : ""}
            {` · ${Math.round(photo.byteSize / 1024)} kB`}
          </div>
        </div>
        <button ref={closeRef} type="button" className={styles.closeBtn} onClick={onClose}>
          {t("common.close")}
        </button>
      </div>

      <div className={styles.lightBody}>
        {url
          ? <img className={styles.lightImg} src={url}
                 alt={t("slotPhoto.alt", { address: photo.address })} />
          : <span className={styles.chosenNote}>
              {failed ? t("slotPhoto.failed") : t("common.loading")}
            </span>}
      </div>

      <div className={styles.lightFoot}>
        <div className={styles.filmstrip}>
          {photos.map((p, i) => (
            <Thumb key={p.id} siteId={siteId} warehouseId={warehouseId} photo={p}
                   onOpen={() => onIndex(i)} />
          ))}
        </div>
        <div className={styles.footActions}>
          <SourceBadge source={photo.addressSource} />
          {photo.chosen
            ? <span className={styles.chosenNote}>
                {t(`slotPhoto.chosenReason.${photo.chosenReason ?? "auto"}`)}
              </span>
            : <button type="button" className={styles.chooseBtn}
                      onClick={() => choose.mutate()} disabled={choose.isPending}>
                {t("slotPhoto.choose")}
              </button>}
        </div>
      </div>
    </div>,
    document.body,
  );
}

/** The lead frame at panel width — the whole point of the section. A 76 px
 *  thumbnail costs a click to learn anything, which makes it a link pretending
 *  to be an image. */
function Hero({
  siteId, warehouseId, photo, count, onOpen,
}: {
  siteId: string; warehouseId: string; photo: SlotPhotoRow;
  count: number; onOpen: () => void;
}) {
  const { t } = useTranslation();
  const { url, failed } = usePhotoObjectUrl(siteId, warehouseId, photo.id);

  return (
    <button type="button" className={styles.hero} onClick={onOpen}
            aria-label={t("slotPhoto.open", { address: photo.address })}>
      {url
        ? <img className={styles.heroImg} src={url} alt="" />
        : <span className={styles.heroPending}>
            {failed ? t("slotPhoto.failed") : t("common.loading")}
          </span>}
      {count > 1 && (
        <span className={styles.heroCount}
              aria-label={t("slotPhoto.countAria", { count })}>{count}</span>
      )}
    </button>
  );
}

/**
 * The photo section of a slot inspector: **one** frame, at the panel's full
 * width, with a floating count when the address has more. Clicking opens the
 * full frame and the rest of the set.
 *
 * Renders nothing at all when the address has no photo — an empty "no photos"
 * block is a row of dead space, and today most addresses have none.
 */
export function SlotPhotoStrip({
  siteId, warehouseId, address, runId,
}: {
  siteId: string; warehouseId: string; address: string; runId?: string | null;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState<number | null>(null);
  // The server gates on write access (TenantContext.assertWriteAccess); this mirrors
  // it so a viewer is not offered a button that will 403. Supervisor is the same bar
  // as acknowledging an alert and justifying a divergence (ADR 283) — the people who
  // walk the floor are the people with a camera.
  const canUpload = useSiteStore((st) => st.roleRank >= ROLE_RANK.supervisor);

  const { data } = useQuery({
    queryKey: ["slot-photos", siteId, warehouseId, address],
    queryFn: () => listSlotPhotos(siteId, warehouseId, { address }),
    enabled: !!siteId && !!warehouseId && !!address,
    staleTime: 60_000,
  });

  // Photos are bound to the association, not the run (ADR 269): a frame with no
  // runId is still this address's evidence. Filter only when the caller asked
  // for one run AND the row carries one, so an older manual upload is not hidden.
  const photos = (data ?? []).filter((p) => !runId || !p.runId || p.runId === runId);

  // ADR 296: the strip survives an empty set now, because the ADD control is not
  // bound to the photos — it is bound to the ADDRESS, which always exists here.
  // That is the line ADR 227 actually draws: hide a control whose DATA is absent,
  // not an action whose target is on screen. A bin with no photo is exactly where
  // someone needs the button.
  if (!photos.length && !canUpload) return null;

  // The chosen frame is the address's evidence (ADR 269). With none chosen the
  // newest wins — the list arrives ordered by takenAt DESC.
  const lead = Math.max(0, photos.findIndex((p) => p.chosen));

  return (
    <div className={styles.strip}>
      <span className={styles.stripTitle}>{t("slotPhoto.title")}</span>
      {photos.length > 0 && (
        <Hero siteId={siteId} warehouseId={warehouseId} photo={photos[lead]}
              count={photos.length} onOpen={() => setOpen(lead)} />
      )}
      {canUpload && (
        <SlotPhotoUpload siteId={siteId} warehouseId={warehouseId}
                         address={address} runId={runId} hasPhotos={photos.length > 0} />
      )}
      {open != null && photos.length > 0 && (
        <Lightbox siteId={siteId} warehouseId={warehouseId} photos={photos}
                  index={open} onIndex={setOpen} onClose={() => setOpen(null)} />
      )}
    </div>
  );
}

/**
 * ADR 296: attach a photo to this address by hand.
 *
 * The server door has been open since ADR 268 — `POST …/slot-photos` with
 * `addressSource` defaulting to `manual` — and only the web control was missing, so
 * every photo in the system had to come from the kit. Not every site has the addon,
 * and not every question waits for the next walk.
 *
 * A plain `<input type="file">` behind a label: no drag-drop zone, no preview step, no
 * crop. The operator is on a warehouse floor with a phone, and the fastest path from
 * "this bin looks wrong" to evidence is one tap to the camera roll.
 */
function SlotPhotoUpload({
  siteId, warehouseId, address, runId, hasPhotos,
}: {
  siteId: string; warehouseId: string; address: string;
  runId?: string | null; hasPhotos: boolean;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);

  const upload = useMutation({
    mutationFn: (file: File) => uploadSlotPhoto(siteId, warehouseId, { file, address, runId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["slot-photos", siteId, warehouseId, address] });
      // The run report counts photos per run; it must not keep a stale total.
      qc.invalidateQueries({ queryKey: ["slot-photos", siteId, warehouseId], exact: false });
    },
  });

  return (
    <div className={styles.uploadRow}>
      <label className={styles.uploadBtn}>
        {upload.isPending ? t("slotPhoto.uploading")
                          : t(hasPhotos ? "slotPhoto.addAnother" : "slotPhoto.add")}
        <input
          ref={inputRef}
          type="file"
          // capture: on a phone this offers the camera directly. A desktop browser
          // ignores it and shows the file picker, so one control serves both.
          accept="image/jpeg,image/png,image/webp"
          capture="environment"
          className={styles.uploadInput}
          disabled={upload.isPending}
          onChange={(e) => {
            const file = e.target.files?.[0];
            // Reset first: picking the SAME file twice after a failure fires no
            // change event otherwise, and the retry looks dead.
            e.target.value = "";
            if (file) upload.mutate(file);
          }}
        />
      </label>
      {upload.isError && (
        <span className={styles.uploadError}>
          {t("slotPhoto.uploadFailed", { msg: (upload.error as Error).message })}
        </span>
      )}
    </div>
  );
}
